// Export sheet — turn a finished app into real, ownable code.
//
// Two ways out: push straight to a brand-new GitHub repository (a Pro
// feature — needs a fine-grained personal access token, used per-request
// and never stored), or download the project as a .tar to push by hand.
// The GitHub push is paywalled; free users get a playful Pro upsell.

import { useEffect, useState } from 'preact/hooks';
import { t } from '../lib/i18n';
import { createPortal } from 'preact/compat';
import type { JSX } from 'preact';
import { X, Check, Download, Github, Lock, ExternalLink } from 'lucide-preact';
import { useAuth } from '@vibeshell/hooks';
import type { RecentApp } from '../lib/recent-apps';

type GhStage = 'form' | 'pushing' | 'pushed';

interface PushResult {
  owner: string;
  repo: string;
  htmlUrl: string;
  branch: string;
}

function slugify(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48) || 'pomela-app'
  );
}

/** Friendly copy for the error codes POST /v1/apps/github can return. */
function githubErrorCopy(code: unknown): string {
  switch (code) {
    case 'bad_token':
      return t('ex.errToken');
    case 'token_forbidden':
      return t('ex.errPerms');
    case 'repo_exists':
      return t('ex.errRepoName');
    case 'missing_token':
      return t('ex.errNoToken');
    case 'missing_jsx':
      return t('ex.errNoCode');
    case 'pro_required':
      return t('ex.errPro');
    default:
      return t('ex.errGeneric');
  }
}

export function ExportSheet({
  app,
  onClose
}: {
  app: RecentApp;
  onClose: () => void;
}): JSX.Element {
  const { session } = useAuth();
  const isPro = session.plan === 'pro';
  const slug = slugify(app.manifest.name);

  const [ghStage, setGhStage] = useState<GhStage>('form');
  const [token, setToken] = useState('');
  const [repoName, setRepoName] = useState(slug);
  const [isPublic, setIsPublic] = useState(false);
  const [result, setResult] = useState<PushResult | null>(null);
  const [ghError, setGhError] = useState('');

  const [downloading, setDownloading] = useState(false);
  const [dlError, setDlError] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  async function pushToGitHub(): Promise<void> {
    if (!app.jsx) {
      setGhError(t('ex.errNoCode'));
      return;
    }
    if (!token.trim()) {
      setGhError(t('ex.errNoToken'));
      return;
    }
    setGhStage('pushing');
    setGhError('');
    try {
      const res = await fetch('/v1/apps/github', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          token: token.trim(),
          jsx: app.jsx,
          theme: app.manifest,
          prompt: app.prompt,
          url: app.url,
          repoName: repoName.trim() || undefined,
          private: !isPublic
        })
      });
      const data = (await res.json().catch(() => ({}))) as Partial<PushResult> & {
        ok?: boolean;
        error?: string;
      };
      if (!res.ok || !data.ok || !data.htmlUrl) {
        setGhError(githubErrorCopy(data.error));
        setGhStage('form');
        return;
      }
      setResult({
        owner: data.owner ?? '',
        repo: data.repo ?? repoName,
        htmlUrl: data.htmlUrl,
        branch: data.branch ?? 'main'
      });
      setToken(''); // the PAT has done its job — drop it from memory
      setGhStage('pushed');
      window.posthog?.capture('app_exported_github', { appId: app.appId });
    } catch {
      setGhError(t('ex.errReach'));
      setGhStage('form');
    }
  }

  async function downloadProject(): Promise<void> {
    if (!app.jsx) {
      setDlError(t('ex.errNoCode'));
      return;
    }
    setDownloading(true);
    setDlError('');
    try {
      // §EXP-007 — read the app's CURRENT Style Pack before exporting, so the download ships its own
      // typography instead of the hardcoded Inter every export used to carry. Best-effort: an app
      // with no pack, or a surface we cannot read, exports exactly as it did before.
      let style: unknown = null;
      try {
        const sres = await fetch(`/v1/edit/${encodeURIComponent(app.appId)}/spec`, { credentials: 'include' });
        if (sres.ok) {
          const sj = (await sres.json()) as { surface?: { style?: { packId: string; intensity: string; themeMode: string | null } | null } };
          const st = sj.surface?.style;
          if (st) style = { stylePackId: st.packId, styleIntensity: st.intensity, ...(st.themeMode ? { themeMode: st.themeMode } : {}) };
        }
      } catch { /* the export must not fail because the style lookup did */ }

      const res = await fetch('/v1/apps/export', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          jsx: app.jsx,
          theme: app.manifest,
          prompt: app.prompt,
          url: app.url,
          style
        })
      });
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = `${slug}.tar`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } catch {
      setDlError(t('ex.errDl'));
    }
    setDownloading(false);
  }

  function goUpgrade(): void {
    onClose();
    location.hash = 'upgrade';
  }

  return createPortal(
    <div className="animate-vs-fade-in" style={overlay} onClick={onClose}>
      <div
        style={card}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t('ex.ariaExport')}
      >
        <div style={grabHandle} aria-hidden="true" />
        <div style={head}>
          <span style={{ fontSize: 15, fontWeight: 700 }}>{t('ex.title')}</span>
          <button type="button" onClick={onClose} aria-label={t('common.close')} style={closeBtn}>
            <X size={16} />
          </button>
        </div>

        {/* ── GitHub push — Pro only ──────────────────────────────────── */}
        {!isPro ? (
          <div style={lockCard}>
            <span style={lockChip}>
              <Lock size={14} />
            </span>
            <p style={{ fontSize: 13.5, fontWeight: 700, margin: '10px 0 4px' }}>
              GitHub export is a Pro perk
            </p>
            <p style={{ ...bodyText, margin: '0 0 14px' }}>
              Push <strong style={{ color: 'var(--fg)' }}>{app.manifest.name}</strong> to a real
              GitHub repo, your code, version-controlled and yours to keep.
            </p>
            <button type="button" onClick={goUpgrade} style={primaryBtn}>
              Upgrade to Pro
            </button>
          </div>
        ) : ghStage === 'pushed' && result ? (
          <div>
            <div style={successRow}>
              <span style={checkChip}>
                <Check size={13} strokeWidth={3.5} />
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 650 }}>{t('ex.pushed')}</div>
                <div style={repoLink}>
                  {result.owner ? `${result.owner}/` : ''}
                  {result.repo}
                </div>
              </div>
            </div>
            <a
              href={result.htmlUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...primaryBtn, textDecoration: 'none' }}
            >
              <Github size={15} /> Open on GitHub <ExternalLink size={13} />
            </a>
          </div>
        ) : (
          <div>
            <p style={bodyText}>
              Push <strong style={{ color: 'var(--fg)' }}>{app.manifest.name}</strong> straight to a
              new GitHub repository.
            </p>
            <label style={fieldLabel}>GitHub access token</label>
            <input
              type="password"
              autoComplete="off"
              spellcheck={false}
              value={token}
              onInput={(e) => setToken((e.currentTarget as HTMLInputElement).value)}
              placeholder="github_pat_…"
              style={field}
            />
            <p style={hint}>
              Create a fine-grained token at{' '}
              <a
                href="https://github.com/settings/tokens?type=beta"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--color-primary)' }}
              >
                github.com/settings/tokens
              </a>{' '}
              with permission to create repositories. It’s used once and never stored.
            </p>
            <label style={fieldLabel}>{t('ex.repoName')}</label>
            <input
              type="text"
              value={repoName}
              onInput={(e) => setRepoName((e.currentTarget as HTMLInputElement).value)}
              placeholder="my-app"
              style={field}
            />
            <label style={checkRow}>
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic((e.currentTarget as HTMLInputElement).checked)}
              />
              <span>{t('ex.makePublic')}</span>
            </label>
            <button
              type="button"
              onClick={pushToGitHub}
              disabled={ghStage === 'pushing'}
              style={{ ...primaryBtn, opacity: ghStage === 'pushing' ? 0.7 : 1 }}
            >
              <Github size={15} />
              {ghStage === 'pushing' ? 'Creating repository…' : 'Create repository & push'}
            </button>
            {ghError && <p style={errLine}>{ghError}</p>}
          </div>
        )}

        {/* ── Download fallback — always available ────────────────────── */}
        <div style={divider} />
        <p style={{ ...bodyText, margin: '0 0 10px' }}>
          Prefer to push it yourself? Download the project, then run these commands.
        </p>
        <button
          type="button"
          onClick={downloadProject}
          disabled={downloading}
          style={{ ...secondaryBtn, opacity: downloading ? 0.7 : 1 }}
        >
          <Download size={15} />
          {downloading ? 'Preparing…' : 'Download project (.tar)'}
        </button>
        <pre style={codeBlock}>{`mkdir ${slug} && tar -xf ${slug}.tar -C ${slug}
cd ${slug}
git init && git add .
git commit -m "Initial commit"`}</pre>
        {dlError && <p style={errLine}>{dlError}</p>}
      </div>
    </div>,
    document.body
  );
}

// ─── styles ─────────────────────────────────────────────────────────────────

const overlay: JSX.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1000,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  background: 'var(--scrim)',
  backdropFilter: 'var(--scrim-blur)'
};

const card: JSX.CSSProperties = {
  width: '100%',
  maxWidth: 440,
  maxHeight: '90dvh',
  overflowY: 'auto',
  background: 'var(--bg-elevated)',
  border: '1px solid var(--border)',
  borderBottom: 'none',
  borderTopLeftRadius: 'var(--radius-2xl)',
  borderTopRightRadius: 'var(--radius-2xl)',
  padding: '10px 20px calc(env(safe-area-inset-bottom) + 20px)',
  boxShadow: 'var(--shadow-lg)'
};

const grabHandle: JSX.CSSProperties = {
  width: 36,
  height: 4,
  borderRadius: 'var(--radius-full)',
  background: 'var(--border-strong)',
  margin: '0 auto 12px'
};

const head: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 14
};

const closeBtn: JSX.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--fg-muted)',
  cursor: 'pointer',
  padding: 4,
  display: 'grid',
  placeItems: 'center'
};

const bodyText: JSX.CSSProperties = {
  fontSize: 13.5,
  lineHeight: 1.55,
  color: 'var(--fg-muted)',
  margin: '0 0 16px'
};

const primaryBtn: JSX.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: 'var(--radius-md)',
  border: 'none',
  background: 'var(--color-primary)',
  color: 'var(--color-on-primary)',
  fontSize: 14,
  fontWeight: 650,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8
};

const secondaryBtn: JSX.CSSProperties = {
  width: '100%',
  padding: '11px 16px',
  borderRadius: 11,
  border: '1px solid var(--border)',
  background: 'var(--bg-subtle)',
  color: 'var(--fg)',
  fontSize: 13.5,
  fontWeight: 600,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8
};

const lockCard: JSX.CSSProperties = {
  textAlign: 'center',
  padding: '20px 16px',
  borderRadius: 14,
  background: 'var(--bg-subtle)',
  border: '1px solid var(--border)'
};

const lockChip: JSX.CSSProperties = {
  display: 'inline-grid',
  placeItems: 'center',
  width: 34,
  height: 34,
  borderRadius: 9999,
  background: 'var(--color-primary)',
  color: 'var(--color-on-primary)'
};

const fieldLabel: JSX.CSSProperties = {
  display: 'block',
  fontSize: 11.5,
  fontWeight: 650,
  color: 'var(--fg-muted)',
  margin: '0 0 5px'
};

const field: JSX.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px solid var(--border)',
  background: 'var(--bg)',
  color: 'var(--fg)',
  fontSize: 13.5,
  marginBottom: 8
};

const hint: JSX.CSSProperties = {
  fontSize: 11,
  lineHeight: 1.5,
  color: 'var(--fg-subtle)',
  margin: '0 0 14px'
};

const checkRow: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 12.5,
  color: 'var(--fg-muted)',
  margin: '0 0 14px',
  cursor: 'pointer'
};

const divider: JSX.CSSProperties = {
  height: 1,
  background: 'var(--border)',
  margin: '18px 0'
};

const successRow: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '12px 14px',
  borderRadius: 12,
  background: 'var(--bg-subtle)',
  border: '1px solid var(--border)',
  marginBottom: 14
};

const checkChip: JSX.CSSProperties = {
  flexShrink: 0,
  width: 24,
  height: 24,
  borderRadius: 9999,
  background: 'var(--color-success)',
  color: 'var(--color-on-primary)',
  display: 'grid',
  placeItems: 'center'
};

const repoLink: JSX.CSSProperties = {
  fontSize: 12,
  color: 'var(--fg-muted)',
  fontFamily: "'SF Mono', ui-monospace, Menlo, Consolas, monospace",
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

const codeBlock: JSX.CSSProperties = {
  margin: '12px 0 0',
  padding: '12px 14px',
  borderRadius: 10,
  background: 'var(--bg-subtle)',
  border: '1px solid var(--border)',
  fontSize: 11.5,
  lineHeight: 1.7,
  color: 'var(--fg)',
  fontFamily: "'SF Mono', ui-monospace, Menlo, Consolas, monospace",
  overflowX: 'auto',
  whiteSpace: 'pre'
};

const errLine: JSX.CSSProperties = {
  fontSize: 12.5,
  color: 'var(--color-danger)',
  margin: '12px 0 0'
};
